import clsx from 'clsx';
import { Individual as ABoxIndividual } from '@semantical/modules/knowledge-graph';
import type { Individual } from '@semantical/modules/semantical-core';
import { RestrictedObjectProperty } from '@semantical/modules/ontology-model';
import { formatObjectPropertyCardinality } from './utils';

type Props = {
    restriction: RestrictedObjectProperty;
    individual: ABoxIndividual;
    availableTargets: Individual[];
    currentValues: Individual[];
    index: number;
    onValueChange: (individual: ABoxIndividual, restriction: RestrictedObjectProperty, targetUri: string | null) => void;
    onValueRemove: (individual: ABoxIndividual, restriction: RestrictedObjectProperty, targetUri: string) => void;
};

export default function EditableObjectProperty({
    restriction,
    individual,
    availableTargets,
    currentValues,
    index,
    onValueChange,
    onValueRemove
}: Props) {
    const maxCardinality = restriction.cardinality.max;
    const canAddMore = maxCardinality === '*' || currentValues.length < maxCardinality;

    const selectedUris = new Set(currentValues.map(v => v.uri));
    const remainingTargets = availableTargets.filter(t => !selectedUris.has(t.uri));
    const hasRemainingTargets = remainingTargets.length > 0;

    const targetClassName = restriction.targetClassLabel || 'individual';
    const placeholderText = hasRemainingTargets
        ? `Add ${targetClassName}...`
        : `No ${targetClassName} available`;

    return (
        <div key={`editable-obj-${index}`} className="flex items-center gap-1.5 mb-1.5 py-0.5">
            <span className="text-[11px] text-slate-600 dark:text-white/75 min-w-20 flex-shrink-0">{restriction.label}:</span>

            <div className="flex flex-col gap-1 flex-1 min-w-0 items-stretch">
                {currentValues.map((value) => (
                    <div
                        key={value.uri}
                        className="inline-flex items-center gap-1 px-2 py-[3px] bg-[rgba(100,180,255,0.15)] border border-[rgba(100,180,255,0.3)] rounded-xl text-[11px] text-[rgba(100,180,255,0.95)] cursor-pointer transition-all duration-150 flex-1 min-w-0"
                        title={value.name}
                    >
                        <span className="text-[10px] opacity-80 flex-shrink-0">ℹ</span>
                        <span className="overflow-hidden text-ellipsis whitespace-nowrap flex-1">{value.name}</span>
                        <button
                            className="ml-1 px-0.5 text-[10px] bg-transparent border-none text-[rgba(255,100,100,0.7)] cursor-pointer leading-none"
                            tabIndex={-1}
                            onClick={() => onValueRemove(individual, restriction, value.uri)}
                            title="Remove"
                        >
                            ×
                        </button>
                    </div>
                ))}

                {canAddMore && (
                    <select
                        value=""
                        disabled={!hasRemainingTargets}
                        className={clsx(
                            "flex-1 px-1.5 py-[3px] text-[11px] bg-slate-100 dark:bg-white/10 border border-slate-200 dark:border-white/20 rounded text-slate-700 dark:text-white outline-none min-w-[100px] cursor-pointer",
                            !hasRemainingTargets && "opacity-50 cursor-not-allowed"
                        )}
                        onChange={(e) => {
                            if (e.target.value) {
                                onValueChange(individual, restriction, e.target.value);
                            }
                        }}
                    >
                        <option value="">{placeholderText}</option>
                        {remainingTargets.map(target => (
                            <option key={target.uri} value={target.uri}>
                                {target.name}
                            </option>
                        ))}
                    </select>
                )}
            </div>

            <span className="text-[9px] text-slate-400 dark:text-white/40 flex-shrink-0">
                {formatObjectPropertyCardinality(restriction)}
            </span>
        </div>
    );
}
